/**
 * Activity tracking: the frontend half of the Activity History feature. A user action (a click
 * on a tracked button) opens an activity scope; every API request made while the scope is open
 * carries the X-Activity-* headers, so the backend can group the resulting changes, jobs and
 * emails under one entry of the subject's history.
 */

/**
 * The kinds of record that have a history. Only IME is captured by the backend today; the
 * others are reserved (see ACTIVITY_SUBJECT_TYPES for what the tracker accepts).
 */
export type ActivitySubjectType =
  | 'IME'
  | 'SUPP'
  | 'INVOICE'
  | 'CLIENT'
  | 'CASE_MANAGER'
  | 'SPECIALIST'
  | 'COMPANY'

/** The subject types the backend has registered: an action on any other type is not tagged. */
export const ACTIVITY_SUBJECT_TYPES: ReadonlyArray<ActivitySubjectType> = [
  'IME',
]

/** The record an action is about. `id` is absent while the record is being created. */
export interface ActivitySubject {
  type: ActivitySubjectType
  id?: string | null
}

/** One entry of the action catalog. */
export interface ActionDefinition {
  /** What the history timeline shows for the action, e.g. "Save appointment". */
  label: string
  /** The page area the action belongs to, e.g. "Appointment". */
  area: string
  /** false = the action is not tracked (no headers are sent). Defaults to true. */
  enabled?: boolean
  description?: string
}

/** An open activity: what the headers of the requests made during it say. */
export interface ActivityScope {
  /** uuid v4, sent as X-Activity-Id. One per click. */
  readonly id: string
  /** The catalog name, sent as X-Activity-Name. */
  readonly name: string
  readonly subject: Readonly<ActivitySubject>
  /** Epoch milliseconds. */
  readonly startedAt: number
}
